import type { GraphQLError } from "graphql/error/GraphQLError";
import { useGpToasts } from "@gportal/uikit";

import * as Sentry from "@sentry/vue";
import { graphQLErrorCodes } from "@/util/error";
import { usePermissions } from "@/util/permissions";
import { useI18n } from "vue-i18n";

// N1 (TV-1757 NGP review): a customer-facing addon refusal travels as a machine-readable extensions code
// (set by the mono/WI-mapping helpers in schema/games/farming_simulator). Map each code to a Loco key; when
// the key is not seeded yet the backend's own message string (deliberately customer-safe copy) is shown.
// Any error WITHOUT a known code keeps the previous behaviour — raw GraphQL string to admins only, generic
// toast to everyone else — so an internal/unexpected error is never leaked to a customer.
const CODE_MESSAGE_KEYS: Record<string, string> = {
  PENDING_CANCELLATION: "ngp/addons/error/pending_cancellation",
  INSUFFICIENT_BALANCE: "ngp/addons/error/insufficient_balance",
  ADDON_NOT_AVAILABLE: "ngp/addons/error/not_available",
  NO_MATCHING_CHANGE: "ngp/addons/error/no_matching_change",
  STALE_STATE: "ngp/addons/error/stale_state",
  TRANSIENT_FAULT: "ngp/addons/error/transient_fault",
  CONFIG_ERROR: "ngp/addons/error/config_error",
  SESSION_EXPIRED: "ngp/addons/error/session_expired",
  INVALID_UNITS: "ngp/addons/error/invalid_units",
};

type ApolloErrorLike = {
  operation?: { operationName?: string };
  graphQLErrors?: readonly GraphQLError[];
  networkError?: unknown;
};

/** Matches `CUSTOMER_FACING_CODE` in `schema/configs.py`. */
export const CUSTOMER_FACING_MESSAGE_CODE = "CUSTOMER_FACING_MESSAGE";

export function useApolloErrorHandling() {
  const { createToast } = useGpToasts();
  const { isAdmin } = usePermissions();
  const { t, te } = useI18n();

  // Returns the customer-facing text for the first graphQLError carrying a known code, or undefined when
  // none is coded. Localised when the Loco key is seeded, otherwise the backend's customer-safe message.
  const codedMessage = (graphQLErrors?: readonly GraphQLError[]): string | undefined => {
    if (!graphQLErrors?.length) return undefined;
    for (const graphQLError of graphQLErrors) {
      const code = graphQLErrorCodes([graphQLError])[0];
      const key = code ? CODE_MESSAGE_KEYS[code] : undefined;
      if (key) return te(key) ? t(key) : graphQLError.message;
    }
    return undefined;
  };

  /**
   * Default handler for Apollo errors that displays a toast notification. Accepts either a thrown
   * ApolloError (mutations) or a plain `{ graphQLErrors }` built from a query's `response.errors`
   * (errorPolicy "all" reports query errors there instead of throwing).
   */
  const handleApolloErrorDefault = ({ operation, graphQLErrors, networkError }: ApolloErrorLike) => {
    Sentry.setTag("apollo-error", true);

    if (isAdmin.value) {
      if (graphQLErrors) {
        graphQLErrors.forEach(function ({ message, locations, path }) {
          console.warn(
            `[GraphQL error] in operation ${operation?.operationName}: ${message},\nLocation: ${locations},\nPath: ${path}`,
          );
        });
      } else if (networkError) {
        console.warn(`[Network error] in operation ${operation?.operationName}: ${networkError}`);
      } else {
        console.warn(`[Unknown error] in operation ${operation?.operationName}`);
      }
    }

    // Create toast message for the error.
    let text = t("ngp/notification/error/text");
    if (networkError) {
      text = `Network error: ${networkError}`;
    } else {
      // A coded refusal and a message tagged CUSTOMER_FACING_MESSAGE both carry customer-safe copy,
      // so they are shown to EVERYONE (N1). An uncoded, untagged error stays admin-only (its raw
      // string may leak internals), with the generic toast for customers.
      const coded = codedMessage(graphQLErrors);
      const customerFacing = graphQLErrors?.filter((error) => error.extensions?.code === CUSTOMER_FACING_MESSAGE_CODE);
      if (coded) {
        text = coded;
      } else if (customerFacing?.length) {
        text = customerFacing.map((error) => error.message).join("\n");
      } else if (isAdmin.value && graphQLErrors) {
        text = graphQLErrors.map((error: Error) => `${error.message}`).join("\n");
      }
    }

    createToast({
      title: t("ngp/notification/error/title"),
      text: text,
      toastType: "danger",
      timeout: 7000,
    });
  };

  return {
    handleApolloErrorDefault,
  };
}
